import { useCallback } from 'react';
import { useAdminUploaderContext } from 'containers/pages/CANguard/context/AdminUploaderProvider';
import { getAdminUploadUppy } from 'containers/pages/CANguard/utils/getCheckpointUppyInstance';
import { useScheduleContext } from 'containers/pages/CANguard/context/ScheduleProvider';
import { useNotificationProvider } from 'hooks';

/**
 * useUploadDDSFile - A custom hook to manage the Uppy instance for file uploads and handle the upload button click event.
 *
 * @param {ScheduleAssignment} row - The schedule assignment object.
 * @returns {Object} - An object containing the Uppy instance and the function to handle the upload button click event.
 *
 * @example
 * const handleUploadButtonClick = useUploadDDSFile(patrol);
 *
 * <IconButton
 *   onClick={(e) => {
 *     e.stopPropagation();
 *     handleUploadButtonClick(patrol);
 *   }}
 * >
 *   <CloudUploadIcon />
 * </IconButton>
 *
 * @param {ScheduleAssignment} patrol - The schedule assignment object passed to the function when called.
 */

const useUploadDDSFile = (patrol: ScheduleAssignment) => {
	const { uppyInstances, setUppyInstances, openDashboard } =
		useAdminUploaderContext();
	const { updatePatrolInSchedule } = useScheduleContext();
	const { notifyError } = useNotificationProvider();

	const handleUploadButtonClick = useCallback(
		(patrol: ScheduleAssignment) => {
			let uppyInstance = uppyInstances[patrol.schedule_assignment_id];
			if (!patrol.comment_list_id) {
				notifyError(
					'Patrol not started yet. Please start the patrol before uploading DDS files.',
				);
				return;
			}
			if (!uppyInstance) {
				uppyInstance = getAdminUploadUppy(patrol);
				uppyInstance.on('complete', (result: any) => {
					const newAttachments: PatrolAttachment[] =
						result.successful.map((file: any) => ({
							AttachmentID: file.response.body.Payload.FileId,
							Name: file.response.body.Payload.Name,
							Size: file.size,
							ContentType: file.type,
						}));
					updatePatrolInSchedule({
						...patrol,
						dds_files: [
							...(patrol.dds_files || []),
							...newAttachments,
						],
					});
				});
				setUppyInstances((prev) => ({
					...prev,
					[patrol.schedule_assignment_id]: uppyInstance,
				}));
			}
			openDashboard(uppyInstance);
		},
		[
			uppyInstances,
			setUppyInstances,
			openDashboard,
			updatePatrolInSchedule,
		],
	);

	return {
		handleUploadButtonClick,
		uppyInstance: uppyInstances[patrol.schedule_assignment_id],
	};
};

export default useUploadDDSFile;
